import React from "react";
import { useGetParams } from "@hooks";
import { Button } from "primereact/button";
import { PickNullable } from "@interfaces";
import { DialogProps } from "primereact/dialog";
import { useForm, useWatch } from "react-hook-form";
import {
  Form,
  Dialog,
  InputText,
  MultiSelect,
  InputTextarea,
} from "@components";

import { ICatalogoCuentaDetalles } from "../../interfaces";
import { useAccountingQuery, useAccountingMutation } from "../../hooks";

interface IEstadoFinancieroDivision {
  Codigo: string;
  Nombre: string;
  EstadoFinancieroId: number;
  EstadoFinancieroDivisionId: number;
}

interface IEstadoFinanciero {
  Nombre: string;
  EstadoFinancieroId: number;
  TipoEstadoFinancieroId: number;
  EstadoFinancieroDivisiones: IEstadoFinancieroDivision[];
}

type FormValues = PickNullable<
  ICatalogoCuentaDetalles,
  | "Nivel"
  | "Activa"
  | "Cuenta"
  | "CuentaId"
  | "EstadoId"
  | "EsControl"
  | "CatalogoId"
  | "Descripcion"
  | "CuentaNombre"
  | "NaturalezaId"
  | "CuentaPadreId"
  | "Clasificacion"
  | "DivisionesIds"
  | "PermiteCambios"
>;

const model = {
  to: (data: FormValues): ICatalogoCuentaDetalles => {
    // console.log(data, "data del to");
    return {
      Nivel: 0,
      EstadoId: 1,
      Activa: true,
      EsControl: true,
      PermiteCambios: true,
      //NaturalezaId: 1,
      Cuenta: data.Cuenta ?? "",
      //Clasificacion: "string",
      CuentaId: data.CuentaId ?? 0,
      CatalogoId: data.CatalogoId ?? 0,
      Descripcion: data.Descripcion ?? "",
      CuentaNombre: data.CuentaNombre ?? "",
      CuentaPadreId: data.CuentaPadreId ?? 0,
      DivisionesIds: data.DivisionesIds ?? [],
    };
  },
  from: (data: ICatalogoCuentaDetalles): FormValues => {
    // console.log(data, "data del from");
    return {
      Nivel: data?.Nivel ?? 0,
      CuentaId: data?.CuentaId,
      EstadoId: data?.EstadoId,
      Cuenta: data?.Cuenta ?? "",
      EsControl: data?.EsControl,
      Activa: data?.Activa ?? true,
      NaturalezaId: data?.NaturalezaId,
      CatalogoId: data?.CatalogoId ?? 0,
      Clasificacion: data?.Clasificacion,
      DivisionesIds: data?.DivisionesIds,
      PermiteCambios: data?.PermiteCambios,
      Descripcion: data?.Descripcion ?? "",
      CuentaNombre: data?.CuentaNombre ?? "",
      CuentaPadreId: data?.CuentaPadreId ?? 0,
    };
  },
};

export interface IAccountCatalogDetailsDialogProps extends DialogProps {
  edit: boolean;
  cuentaPadreId?: number;
}

const AccountCatalogDetailsDialog = ({
  edit,
  cuentaPadreId,
  ...props
}: IAccountCatalogDetailsDialogProps) => {
  const id = useGetParams("id");

  const createCatalogo = useAccountingMutation(
    "AccountCatalog",
    "createDetails",
  );
  const updateCatalogo = useAccountingMutation(
    "AccountCatalog",
    "updateDetails",
  );
  const {
    data: accountCatalogDetalleData,
    isFetching: accountCatalogDetalleFetching,
  } = useAccountingQuery("AccountCatalog", "getUpdateDetalle", cuentaPadreId, {
    enabled: !!cuentaPadreId,
  });

  const { data: cuentaPadreDataNombre } = useAccountingQuery(
    "AccountCatalog",
    "getUpdateDetalle",
    accountCatalogDetalleData?.CuentaPadreId,
    { enabled: !!accountCatalogDetalleData?.CuentaPadreId },
  );

  const form = useForm<FormValues>({
    defaultValues: {
      CatalogoId: id,
    },
  });
  const { reset } = form;

  const selectedDivisiones = useWatch({
    name: "DivisionesIds",
    control: form.control,
  });

  //select divisiones
  const divisionesOption = React.useMemo(() => {
    if (!accountCatalogDetalleData?.DivisionesSelect.length) {
      return [];
    }

    return accountCatalogDetalleData.DivisionesSelect.map(
      (estadoFinanciero: IEstadoFinanciero) => {
        const isDisabled = estadoFinanciero.EstadoFinancieroDivisiones.some(
          (division) =>
            selectedDivisiones?.includes(division.EstadoFinancieroDivisionId),
        );

        return {
          label: estadoFinanciero.Nombre,
          items: estadoFinanciero.EstadoFinancieroDivisiones.map(
            (division) => ({
              label: division.Codigo,
              value: division.EstadoFinancieroDivisionId,
              disabled:
                isDisabled &&
                !selectedDivisiones?.includes(
                  division.EstadoFinancieroDivisionId,
                ),
            }),
          ),
        };
      },
    );
  }, [accountCatalogDetalleData?.DivisionesSelect, selectedDivisiones]);

  //datos en from
  React.useEffect(() => {
    if (edit && accountCatalogDetalleData) {
      const formData = model.from({
        ...accountCatalogDetalleData,
        DivisionesIds: accountCatalogDetalleData.DivisionesIds,
      });
      reset(formData, { keepDefaultValues: true });
    }
    if (!edit) {
      reset();
    }
  }, [accountCatalogDetalleData, edit, reset]);

  const handleOnSuccess = () => {
    reset();
    props.onHide();
  };

  const onSubmit = (values: FormValues) => {
    if (edit) {
      return updateCatalogo.mutateAsync(
        model.to({
          ...values,
          DivisionesIds: selectedDivisiones,
        }),
        { onSuccess: handleOnSuccess },
      );
    }
    return createCatalogo.mutateAsync(
      model.to({ ...values, CuentaPadreId: Number(cuentaPadreId) }),
      { onSuccess: handleOnSuccess },
    );
  };

  return (
    <Dialog
      {...props}
      title={edit ? "Editar cuenta" : "Crear cuenta"}
      onHide={() => {
        form.reset();
        props.onHide();
      }}
      footer={
        <div className="flex flex-1 justify-content-end gap-2">
          <Button
            size="small"
            severity="warning"
            label="Reiniciar formulario"
            disabled={!form.formState.isDirty}
            onClick={() => {
              form.reset();
            }}
          />
          <Button
            size="small"
            onClick={form.handleSubmit(onSubmit)}
            loading={accountCatalogDetalleFetching}
            label={edit ? "Guardar cambios" : "Crear catalogo de cuenta"}
          />
        </div>
      }
    >
      {accountCatalogDetalleData !== undefined &&
      accountCatalogDetalleData.CuentaPadreId !== null ? (
        <div className="flex align-items-center flex-row pb-3">
          <p>Cuenta padre:</p>
          {cuentaPadreDataNombre !== undefined ? (
            <h4 className="pl-2">{cuentaPadreDataNombre.CuentaNombre}</h4>
          ) : null}
        </div>
      ) : null}
      <Form form={form}>
        <InputText
          label="Nombre"
          name="CuentaNombre"
          className="p-inputtext-sm"
          rules={{ required: "Este campo es necesario" }}
        />
        <InputTextarea
          name="Descripcion"
          label="Descripción"
          placeholder="Descripción"
          className="p-inputtext-sm"
          rules={{ required: "Este campo es necesario" }}
        />
        <MultiSelect
          label="Divisiones"
          optionLabel="label"
          name="DivisionesIds"
          optionGroupLabel="label"
          className="p-inputtext-sm"
          options={divisionesOption}
          optionGroupChildren="items"
          placeholder="Selecciona una division"
          rules={{ required: "Este campo es necesario" }}
        />
      </Form>
    </Dialog>
  );
};

export default AccountCatalogDetailsDialog;
